import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "RefugeeVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    refugee_verification_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        personal_details: false,
        status_information: false,
        permit_details: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    safe_refugee_data() {
      return this.refugee_verification_data || {};
    },

    response_data() {
      // Handle both nested (response.X) and direct (X) data structures
      return this.safe_refugee_data.response || this.safe_refugee_data;
    },

    golden_source_data() {
      return this.response_data.GoldenSource || {};
    },

    results_array() {
      return this.golden_source_data.Results || [];
    },

    first_result() {
      return this.results_array.length > 0 ? this.results_array[0] : {};
    },

    has_refugee_data() {
      return this.results_array.length > 0;
    },

    // Personal Information
    first_name() {
      return this.first_result.FirstName || "N/A";
    },

    surname() {
      return this.first_result.Surname || "N/A";
    },

    full_name() {
      return `${this.first_name} ${this.surname}`;
    },

    gender() {
      return this.first_result.Gender || "N/A";
    },

    formatted_gender() {
      return this.gender;
    },

    birth_date() {
      return this.first_result.BirthDate || "N/A";
    },

    formatted_birth_date() {
      return this.formatDate(this.birth_date);
    },

    age() {
      return this.first_result.Age || "N/A";
    },

    country() {
      return this.first_result.Country || "N/A";
    },

    nationality() {
      return this.first_result.Nationality || "N/A";
    },

    niis_file_number() {
      return this.first_result.NIISFileNumber || "N/A";
    },

    alternative_file_number() {
      return this.first_result.AlternativeFileNumber || "N/A";
    },

    refugee_id_number() {
      return this.first_result.RefugeeIDNumber || "";
    },

    // Application & Status Information
    office_of_application() {
      return this.first_result.OfficeOfApplication || "N/A";
    },

    current_status() {
      return this.first_result.Status || "N/A";
    },

    status_badge_class() {
      const status = this.current_status.toLowerCase();
      if (status.includes("approved") || status.includes("granted")) {
        return "badge bg-success";
      } else if (status.includes("pending") || status.includes("application")) {
        return "badge bg-warning text-dark";
      } else if (status.includes("rejected") || status.includes("denied")) {
        return "badge bg-danger";
      } else {
        return "badge bg-secondary";
      }
    },

    requested_by() {
      return this.response_data.RequestedBy || "N/A";
    },

    authorized_by() {
      return this.response_data.AuthorizedBy || "N/A";
    },

    // Permit & Document Details
    permit_number() {
      return this.first_result.PermitNumber || "N/A";
    },

    expiry_date() {
      return this.first_result.ExpiryDate || "N/A";
    },

    formatted_expiry_date() {
      return this.format_expiry_date(this.expiry_date);
    },

    expiry_status_class() {
      if (this.is_expired()) {
        return "text-danger fw-bold";
      } else if (this.is_expiring_soon()) {
        return "text-warning fw-bold";
      } else {
        return "text-success fw-bold";
      }
    },

    customer_reference() {
      return this.response_data.CRef || "N/A";
    },

    facial_image_available() {
      return this.response_data.FacialImageAvailable || false;
    },

    facial_image_status_text() {
      return this.facial_image_available ? "Available" : "Not Available";
    },

    facial_image_status_class() {
      return this.facial_image_available ? "text-success" : "text-muted";
    },

    facial_image_icon() {
      return this.facial_image_available
        ? "bi bi-check-circle"
        : "bi bi-x-circle";
    },

    // Verification Details
    tracking_number() {
      return this.response_data.TrackingNumber || "N/A";
    },

    results_count() {
      return this.results_array.length;
    },

    error_message() {
      return this.golden_source_data.ErrorMessage || "";
    },

    response_message() {
      return this.response_data.Message || "N/A";
    },

    verification_status() {
      return this.response_data.Status || "Unknown";
    },

    verification_status_text() {
      return this.verification_status;
    },

    verification_status_class() {
      const status = this.verification_status.toLowerCase();
      if (status === "success") {
        return "text-success";
      } else if (status === "error" || status === "failed") {
        return "text-danger";
      } else {
        return "text-warning";
      }
    },

    // Export functionality
    has_selected_tables() {
      return Object.values(this.export_options).some((option) => option);
    },

    report_reference() {
      return this.customer_reference || `REF-${Date.now()}`;
    },

    generated_date() {
      return new Date().toLocaleString();
    },
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },

    format_expiry_date(expiry_string) {
      if (!expiry_string || expiry_string === "N/A") return "N/A";

      try {
        // Handle format like "Tuesday, 13/03/2018; 24:00"
        if (expiry_string.includes(",")) {
          const date_part = expiry_string.split(",")[1].trim().split(";")[0];
          const [day, month, year] = date_part.split("/");
          const date = new Date(year, month - 1, day);
          return date.toLocaleDateString("en-GB", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
        return expiry_string;
      } catch (error) {
        return expiry_string;
      }
    },

    is_expired() {
      if (!this.expiry_date || this.expiry_date === "N/A") return false;

      try {
        const expiry_string = this.expiry_date
          .split(",")[1]
          ?.trim()
          .split(";")[0];
        if (!expiry_string) return false;

        const [day, month, year] = expiry_string.split("/");
        const expiry_date = new Date(year, month - 1, day);
        return expiry_date < new Date();
      } catch (error) {
        return false;
      }
    },

    is_expiring_soon() {
      if (!this.expiry_date || this.expiry_date === "N/A") return false;

      try {
        const expiry_string = this.expiry_date
          .split(",")[1]
          ?.trim()
          .split(";")[0];
        if (!expiry_string) return false;

        const [day, month, year] = expiry_string.split("/");
        const expiry_date = new Date(year, month - 1, day);
        const thirty_days_from_now = new Date();
        thirty_days_from_now.setDate(thirty_days_from_now.getDate() + 30);

        return expiry_date > new Date() && expiry_date <= thirty_days_from_now;
      } catch (error) {
        return false;
      }
    },

    show_export_modal() {
      this.export_options.personal_details = true;
      this.export_options.status_information = true;
      this.export_options.permit_details = true;
      this.export_options.raw_json = false;
      $("#export-modal-" + this.report_reference).modal("show");
    },

    close_modal() {
      $("#export-modal-" + this.report_reference).modal("hide");
    },

    async ensure_libraries_loaded() {
      try {
        if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
          if (typeof Papa === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"
            );
          }
          this.libraries_loaded.papa_parse = true;
        }

        if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
          if (typeof XLSX === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
            );
          }
          this.libraries_loaded.sheetjs = true;
        }
      } catch (error) {
        throw new Error("Failed to load export libraries");
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async export_data() {
      try {
        await this.ensure_libraries_loaded();

        const export_data = this.prepare_export_data();

        if (this.export_format === "csv") {
          this.export_as_csv(export_data);
        } else if (this.export_format === "excel") {
          this.export_as_excel(export_data);
        } else if (this.export_format === "json") {
          this.export_as_json(export_data);
        }

        this.close_modal();
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },

    prepare_export_data() {
      const export_data = {};

      if (this.export_options.personal_details) {
        export_data.personal_details = [
          {
            first_name: this.first_name,
            surname: this.surname,
            gender: this.gender,
            birth_date: this.birth_date,
            age: this.age,
            country: this.country,
            nationality: this.nationality,
            niis_file_number: this.niis_file_number,
            alternative_file_number: this.alternative_file_number,
            refugee_id_number: this.refugee_id_number,
          },
        ];
      }

      if (this.export_options.status_information) {
        export_data.status_information = [
          {
            office_of_application: this.office_of_application,
            current_status: this.current_status,
            requested_by: this.requested_by,
            authorized_by: this.authorized_by,
          },
        ];
      }

      if (this.export_options.permit_details) {
        export_data.permit_details = [
          {
            permit_number: this.permit_number,
            expiry_date: this.expiry_date,
            customer_reference: this.customer_reference,
            facial_image_available: this.facial_image_available,
            tracking_number: this.tracking_number,
            results_count: this.results_count,
            error_message: this.error_message,
            response_message: this.response_message,
          },
        ];
      }

      if (this.export_options.raw_json) {
        export_data.raw_json = this.safe_refugee_data;
      }

      return export_data;
    },

    export_as_csv(export_data) {
      var Papa = window.Papa;
      Object.keys(export_data).forEach((table_name) => {
        const data = export_data[table_name];
        if (table_name === "raw_json") {
          const csv_content = Papa.unparse([data]);
          this.download_file(
            `refugee_verification_${table_name}.csv`,
            csv_content
          );
        } else {
          const csv_content = Papa.unparse(data);
          this.download_file(
            `refugee_verification_${table_name}.csv`,
            csv_content
          );
        }
      });
    },

    export_as_excel(export_data) {
      var XLSX = window.XLSX;
      const workbook = XLSX.utils.book_new();

      Object.keys(export_data).forEach((table_name) => {
        const data = export_data[table_name];
        let worksheet;

        if (table_name === "raw_json") {
          worksheet = XLSX.utils.json_to_sheet([data]);
        } else {
          worksheet = XLSX.utils.json_to_sheet(data);
        }

        XLSX.utils.book_append_sheet(workbook, worksheet, table_name);
      });

      XLSX.writeFile(workbook, "refugee_verification_report.xlsx");
    },

    export_as_json(export_data) {
      const json_content = JSON.stringify(export_data, null, 2);
      this.download_file("refugee_verification_report.json", json_content);
    },

    download_file(filename, content) {
      const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
